import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { combineLatest, forkJoin, Observable } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { UserConfigsService } from '@services/user-configs.service';
import { UserConfigs } from '@models/user-configs.model';
import { FilterService } from '@services/filter.service';
import { Filter } from '@models/review/filter.model';
import { OkCancelDialogComponent } from '@dialogs/ok-cancel/ok-cancel-dialog.component';
import { HelpService } from '@services/help.service';
import { AutoRedactionService } from '@services/auto-redaction-service/auto-redaction.service';
import { AutoRedactionJob } from '@models/auto-redaction-job.model';
import { AutoRedactionRule } from '@models/auto-redaction-rule.model';
import { AutoRedactionDetailsComponent } from '../auto-redaction-details/auto-redaction-details.component';
import { RunStatus } from '@models/run-status.enum';
import { SelectFilterNewComponent } from '@dialogs/select-filter-new/select-filter-new.component';
import { ODataModelSanitizerService } from '@services/odata-model-sanitizer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UserManager } from '@managers/user.manager';
import { UserResource } from '@models/resource/user.resource';
import { BaseComponent } from "../../../base.component";
import { CaseHubSdk } from "@sdks/case.hub-sdk";
import { Store } from "@ngrx/store";
import { selectIsProcessing } from "@store/global/global.selectors";

@Component({
    selector: 'lex-auto-redaction',
    templateUrl: './auto-redaction.component.html',
    styleUrls: ['./auto-redaction.component.scss'],
    standalone: false
})
export class AutoRedactionComponent extends BaseComponent implements OnInit {
	public selectedAutoRedactionJob$ = signal<AutoRedactionJob>(null);
	public isProcessing$ = signal<boolean>(false);
	public editAutoRedactionJobId: string = '';
	public autoRedactionJobs: AutoRedactionJob[] = [];
	public userConfig: UserConfigs;
	public filterString: string = '';
	public filter: Filter;
	public isGraphView = true;
	public createdByUsers: UserResource[];
	public RunStatus = RunStatus;

	@ViewChild(AutoRedactionDetailsComponent) detailsComponent: AutoRedactionDetailsComponent;

	constructor(
		public filterService: FilterService,
		private autoRedactionService: AutoRedactionService,
		private userConfigService: UserConfigsService,
		private dialog: MatDialog,
		private userManager: UserManager,
		private sanitizer: ODataModelSanitizerService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private helpService: HelpService,
		private caseHubSdk: CaseHubSdk,
		private store: Store
	) {
		super();

		this.caseHubSdk.autoRedactionJobs$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => this.initializeAutoRedactionJob());

		this.store.select(selectIsProcessing).pipe(
			takeUntil(this.destroy$)
		).subscribe((isProcessing) => this.isProcessing$.set(isProcessing))
	}

	ngOnInit() {
		this.userConfigService.getUserConfigs().subscribe((config: UserConfigs) => {
			this.userConfig = config;
		});
		this.initializeAutoRedactionJob();
		this.helpService.updateHelpLink('analysis-menu/auto-redaction');
	}

	public getCreatedByName(createdById: string): string {
		return this.createdByUsers?.find(u => u.id === createdById)?.userName ?? '';
	}

	public toggleIcon(): string {
		return this.isGraphView ? 'toc' : 'bar_chart';
	}

	public browseJob() {
		this.router.navigate(['review/browse'], {queryParams: {AutoRedactionRuleIds: this.selectedAutoRedactionJob$().autoRedactionRules.map(r => r.id)}});
	}

	public isSelected(autoRedaction: AutoRedactionJob): boolean {
		return this.selectedAutoRedactionJob$() && this.selectedAutoRedactionJob$().id === autoRedaction.id;
	}

	public createAutoRedactionJob() {
		const newAutoRedaction = new AutoRedactionJob();
		newAutoRedaction.title = this.getNextTitle();
		newAutoRedaction.caseId = this.userConfig.currentCase.caseId;
		newAutoRedaction.createdById = this.userConfig.userId;
		newAutoRedaction.autoRedactionRules = new Array<AutoRedactionRule>();
		this.autoRedactionService.createAutoRedactionJob(newAutoRedaction).subscribe((autoRedaction: AutoRedactionJob) => {
			this.handleCreateResponse(autoRedaction);
		});
	}

	public saveAutoRedactionJobChanges(autoRedaction: AutoRedactionJob) {
		this.autoRedactionService.updateAutoRedactionJob(autoRedaction).subscribe(responseJob => {
			const idx = this.autoRedactionJobs.findIndex(j => j.id === responseJob.id);
			this.selectedAutoRedactionJob$.set(responseJob);
			this.autoRedactionJobs[idx] = this.selectedAutoRedactionJob$();
		});

		this.editAutoRedactionJobId = '';
	}

	public getTotalRedactionCount(rules: AutoRedactionRule[]): number {
		return rules.reduce((sum, current) => current.redactionCount + sum, 0);
	}

	public cloneAutoRedaction(fromJob: AutoRedactionJob) {
		const clone = new AutoRedactionJob();
		clone.title = this.getNextTitle();
		clone.caseId = fromJob.caseId;
		clone.filterId = fromJob.filterId;
		clone.createdById = this.userConfig.userId;
		clone.runStatus = RunStatus.created;

		this.autoRedactionService.createAutoRedactionJob(clone).subscribe((newJob) => {
			this.cloneAutoRedactionRules(fromJob, newJob);
		});
	}

	public deleteAutoRedactionJob(autoRedaction: AutoRedactionJob) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Auto Redaction Job`,
			message: `Delete
      ${autoRedaction.title}?
          <br/> The auto redaction job will be permanently deleted from the case.
          <br/> This action cannot be undone.`
		};
		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked: boolean) => {
			if (okClicked) {
				this.detailsComponent.deleteOrUndoRules(this.selectedAutoRedactionJob$().autoRedactionRules.map(r => r.id)).subscribe(ok => {
					if (!ok)
						return;

					this.selectedAutoRedactionJob$.set(null);
					this.autoRedactionService.deleteAutoRedactionJob(autoRedaction.id).subscribe((success) => {
						if (success) {
							this.autoRedactionJobs.splice(this.autoRedactionJobs.indexOf(autoRedaction), 1);
						}
					});
				});
			}
		});
	}

	public runAutoRedactionJob(autoRedactionJob: AutoRedactionJob) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '390px';
		dialogConfig.data = {
			title: 'Confirm Job Run',
			message: 'Confirm queuing job run. <br /> Note: job runs can take up to several hours'
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((data) => {
			if (!data)
				return;
			this.startAutoRedactDocsWorkflow(autoRedactionJob.id);
			autoRedactionJob.runStatus = RunStatus.running;
			autoRedactionJob.dateTimeRun = new Date().toISOString();
			this.autoRedactionService.updateAutoRedactionJob(autoRedactionJob).subscribe(j => autoRedactionJob.lastModified = j.lastModified);
		});
	}

	public selectFilter() {
		const rs = this.selectedAutoRedactionJob$().runStatus;
		if (rs == RunStatus.running || rs == RunStatus.completed)
			return false;
		this.showFilter();
	}

	public setAutoRedactionJob(autoRedactionJob: AutoRedactionJob) {
		if (!autoRedactionJob) {
			this.router.navigate([], {relativeTo: this.activatedRoute});
			this.selectedAutoRedactionJob$.set(null);
			this.editAutoRedactionJobId = null;
		} else {
			this.router.navigate([], {
				relativeTo: this.activatedRoute,
				queryParams: {AutoRedactionJobId: autoRedactionJob.id}
			});
			this.selectedAutoRedactionJob$.set(autoRedactionJob);

			if (this.editAutoRedactionJobId)
				this.editAutoRedactionJobId = autoRedactionJob.id;
			if (autoRedactionJob.filterId) {
				this.filterService.getFilter(this.selectedAutoRedactionJob$().filterId).subscribe((data: Filter) => {
					this.setFilter(data);
				});
			} else {
				this.setFilter(null);
			}
		}
	}

	private initializeAutoRedactionJob() {
		const autoRedactionsObservable = this.autoRedactionService.getAutoRedactionJobs();
		const createdByObservable = this.userManager.getAutoRedactionCreatedBy();
		combineLatest([autoRedactionsObservable, createdByObservable])
			.pipe(take(1))
			.subscribe(data => {
				this.autoRedactionJobs = data[0];
				this.autoRedactionJobs.forEach(j => this.sanitizer.sanitizeJob(j));
				this.createdByUsers = data[1];

				let job;
				if (this.activatedRoute.snapshot.queryParams['AutoRedactionJobId']) {
					job = this.autoRedactionJobs.find(j => j.id == this.activatedRoute.snapshot.queryParams['AutoRedactionJobId']);
				} else if (this.selectedAutoRedactionJob$()) {
					job = this.autoRedactionJobs.find(j => j.id == this.selectedAutoRedactionJob$().id);
				}

				if (job?.id !== this.selectedAutoRedactionJob$()?.id || job?.lastModified !== this.selectedAutoRedactionJob$()?.lastModified) {
					this.selectedAutoRedactionJob$.set(job);
					if (this.selectedAutoRedactionJob$().filterId) {
						this.filterService.getFilter(this.selectedAutoRedactionJob$().filterId).subscribe((data: Filter) => {
							this.setFilter(data);
						});
					} else {
						this.setFilter(null);
				}}
			});
	}

	private handleCreateResponse(job: AutoRedactionJob) {
		if (!this.createdByUsers?.find(u => u.id === this.userConfig.userId))
			this.createdByUsers.push({id: this.userConfig.userId, userName: this.userConfig.userName});
		if (!job.autoRedactionRules)
			job.autoRedactionRules = new Array<AutoRedactionRule>();
		this.autoRedactionJobs.unshift(job);
		this.selectedAutoRedactionJob$.set(job);
	}

	private getNextTitle(): string {
		return `Untitled Job ${this.autoRedactionJobs.length + 1}`;
	}

	private cloneAutoRedactionRules(fromJob: AutoRedactionJob, toJob: AutoRedactionJob): void {
		if (!toJob.autoRedactionRules)
			toJob.autoRedactionRules = new Array<AutoRedactionRule>();

		const observableCollection = new Array<Observable<AutoRedactionRule>>();
		fromJob.autoRedactionRules.forEach(r => {
			observableCollection.push(this.cloneRule(toJob.id, r).pipe(tap(s => toJob.autoRedactionRules.push(s))));
		});

		forkJoin(observableCollection)
			.subscribe(() => {
				this.handleCreateResponse(toJob);
			});
	}

	private cloneRule(jobId: string, source: AutoRedactionRule): Observable<AutoRedactionRule> {
		const clone = new AutoRedactionRule();

		clone.autoRedactionJobId = jobId;
		clone.presetType = source.presetType;
		clone.rulePattern = source.rulePattern;
		clone.ruleTitle = source.ruleTitle;
		clone.redactionLabel = source.redactionLabel;
		clone.redactionColorId = source.redactionColorId;

		return this.autoRedactionService.createAutoRedactionRule(clone);
	}

	private startAutoRedactDocsWorkflow(autoRedactionJobId: string) {
		this.autoRedactionService.startAutoRedactionJobWorkflow(autoRedactionJobId).subscribe();
	}

	private showFilter() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '800px';
		dialogConfig.data = {
			filter: this.filter ? JSON.parse(this.filter.filterJson) : null
		};

		this.dialog.open(SelectFilterNewComponent, dialogConfig).afterClosed().subscribe((data) => {
			if (!data || data.isCancelled)
				return;

			if (data.filter) {
				this.filterService.saveFilter(data.filter).subscribe((filter: Filter) => {
					this.selectedAutoRedactionJob$().filterId = filter.savedFilterId;
					this.setFilter(filter);
					this.saveAutoRedactionJobChanges(this.selectedAutoRedactionJob$());
				});
			} else {
				this.selectedAutoRedactionJob$().filterId = null;
				this.filterString = '';
				this.saveAutoRedactionJobChanges(this.selectedAutoRedactionJob$());
			}
		});
	}

	private setFilter(filter: Filter) {
		this.filter = filter;
		if (filter?.filterText)
			this.filterString = filter.filterText;
	}
}
